import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { paginationParams } from '@shared/config/table.config';
import {
  AccessControlService,
  VendorRepresentative,
} from './../access-control.service';
import { AuthService } from '@core/auth.service';
import { DataService } from '@services/data.service';
import { GlobalErrorService } from '@app/shared/services/global-error.service';
import { ErrorMessages } from '@app/shared/constants/constants';
import {
  TabType,
  SortDirection,
  PageTitles,
  vendorStatus,
  PageTitle,
  NavigationRoutes,
} from '@constants/global.enum';
import { Router } from '@angular/router';

@Component({
  selector: 'app-access-control-page',
  templateUrl: './access-control-page.component.html',
  styleUrls: ['./access-control-page.component.scss'],
})
export class AccessControlPageComponent implements OnInit {
  pageTitle = PageTitles.ACCESS_MANAGEMENT;
  tabs = [
    { label: 'Vendor', value: TabType.VENDOR },
    { label: 'Leaders', value: TabType.LEADERS },
  ];
  activeTab = TabType.VENDOR;
  errorMsg: string = ErrorMessages.NoDataAvailable;
  imageSrc: string = 'assets/img/no-data-found.png';
  OPSAdminPageBreadcrumb = PageTitle;

  columnDefs = [
    {
      field: 'vendorName',
      headerName: 'Vendor Name',
      filterParams: { hideMenu: true },
      sortParams: { enabled: true },
    },
    {
      field: 'pointOfContact',
      headerName: 'Point of Contact',
      filterParams: { hideMenu: true },
    },
    {
      field: 'email',
      headerName: 'Email',
      filterParams: { hideMenu: true },
    },
    {
      field: 'contact',
      headerName: 'Contact',
      filterParams: { hideMenu: true },
    },
    {
      field: 'status',
      headerName: 'Status',
      filterParams: { hideMenu: true },
      sortParams: { enabled: true },
      custom: true,
    },
  ];
  vendorData: (VendorRepresentative & { status?: string })[] = [];
  filteredVendorData: (VendorRepresentative & { status?: string })[] = [];
  filterValue = '';
  addVendorPocModalOpen = false;
  private isRefreshing = false;
  private isStatusUpdating = false;
  paginationParams = {
    ...paginationParams,
    pageIndex: 0,
  };

  confirmationModal = {
    open: false,
    title: '',
    message: '',
    confirmLabel: '',
    error: '',
    row: null as any,
    rowIndex: undefined as number | undefined,
    isCurrentlyActive: false,
    originalStatus: undefined as string | undefined,
  };

  sortField: string = '';
  sortDirection: SortDirection = SortDirection.ASC;
  maxTableHeight: number = 475;

  @ViewChild('addVendorPocComp') addVendorPocComp: any;

  constructor(
    private cdr: ChangeDetectorRef,
    private accessControlService: AccessControlService,
    private authService: AuthService,
    private dataService: DataService,
    private errorHandler: GlobalErrorService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadVendorRepresentatives();
  }

  private loadVendorRepresentatives(): void {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    this.accessControlService.getVendorRepresentatives().subscribe({
      next: (res) => {
        this.vendorData = res.data.map((item) => ({
          ...item,
          status:
            !item.status || item.status === null || item.status === undefined
              ? vendorStatus.Inactive
              : item.status,
        }));
        this.filteredVendorData = [...this.vendorData];
        this.cdr.markForCheck();
        this.isRefreshing = false;
      },
      error: (err) => {
        this.vendorData = [];
        this.filteredVendorData = [];
        this.isRefreshing = false;
      },
    });
  }

  searchVendorPOCs(event: KeyboardEvent): void {
    const searchValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
    this.filterValue = searchValue;
    if (!searchValue) {
      this.filteredVendorData = [...this.vendorData];
      return;
    }
    this.filteredVendorData = this.vendorData.filter((vendor) =>
      Object.values(vendor).some((value) =>
        value?.toString().toLowerCase().includes(searchValue),
      ),
    );
  }

  clearSearch(): void {
    this.filterValue = '';
    this.filteredVendorData = [...this.vendorData];
  }

  openAddVendorPocModal() {
    this.addVendorPocModalOpen = false;
    setTimeout(() => {
      this.addVendorPocModalOpen = true;
    }, 0);
  }

  onStatusToggle(row: any): void {
    if (this.confirmationModal && this.confirmationModal.open) {
      this.confirmationModal.open = false;
      this.cdr.detectChanges(); 
    }
    const pointOfContact = row.pointOfContact || '';
    const vendorName = row.vendorName || '';
    this.confirmationModal.row = { ...row };
    this.confirmationModal.rowIndex = this.filteredVendorData.findIndex(
      (v) => v.email === row.email,
    );
    this.confirmationModal.originalStatus = row.status;
    this.confirmationModal.open = true;
    this.confirmationModal.title =
      row.status && row.status.toLowerCase() === vendorStatus.Active
        ? 'Inactive vendor'
        : 'Active vendor';
    this.confirmationModal.message = `Are you sure you want to remove the access for ${pointOfContact} associated with "${vendorName}"?`;
    this.confirmationModal.confirmLabel =
      row.status && row.status.toLowerCase() === vendorStatus.Active
        ? 'Inactive'
        : 'Active';
    this.cdr.detectChanges();
  }

  closeConfirmationModal(): void {
    this.confirmationModal.open = false;
    this.confirmationModal.row = null;
    this.confirmationModal.rowIndex = undefined;
    this.confirmationModal.title = '';
    this.confirmationModal.message = '';
    this.confirmationModal.confirmLabel = '';
    this.confirmationModal.error = '';
    this.confirmationModal.originalStatus = undefined;
    this.cdr.detectChanges();
  }

  confirmStatusChange(): void {
    const isCurrentlyActive =
      this.confirmationModal.row.status.toLowerCase() === vendorStatus.Active;
    const updatedStatus = isCurrentlyActive
      ? vendorStatus.Inactive
      : vendorStatus.Active;
    const userProfile = this.dataService?.userProfile();
    const payload = {
      email: this.confirmationModal.row.email,
      status: updatedStatus,
      updatedBy: userProfile?.firstName || 'Unknown',
    };
    this.accessControlService
      .saveVendorRepresentative(payload, 'put')
      .subscribe({
        next: () => {
          if (this.confirmationModal.rowIndex !== undefined) {
            const updatedStatusCapitalized =
              updatedStatus.charAt(0).toUpperCase() + updatedStatus.slice(1);
            this.filteredVendorData[this.confirmationModal.rowIndex].status =
              updatedStatusCapitalized;
            console.log(
              this.filteredVendorData[this.confirmationModal.rowIndex],
            );

            const originalIndex = this.vendorData.findIndex(
              (v) => v.email === this.confirmationModal.row.email,
            );
            if (originalIndex !== -1) {
              this.vendorData[originalIndex].status = updatedStatusCapitalized;
            }
          }
          this.cdr.detectChanges();
          this.closeConfirmationModal();
        },
        error: (err) => {
          this.closeConfirmationModal();
          this.fetchVendorData();
        },
      });
  }

  fetchVendorData(): void {
    this.accessControlService.getVendorRepresentatives().subscribe({
      next: (res) => {
        this.vendorData = res.data.map((item) => ({
          ...item,
          status:
            !item.status || item.status === null || item.status === undefined
              ? vendorStatus.Inactive
              : item.status,
        }));
        this.filteredVendorData = this.vendorData;
      },
      error: (err) => {
        this.errorHandler.logError(err);
      },
    });
  }
  onPageChange(event: any) {
    if (
      event &&
      event.pageIndex !== undefined &&
      event.pageSize !== undefined
    ) {
      this.paginationParams.pageIndex = event.pageIndex;
      this.paginationParams.pageSize = event.pageSize;
    }
  }

  closeAddVendorPocModal() {
    this.addVendorPocModalOpen = false;
    setTimeout(() => {
      this.addVendorPocModalOpen = false;
      setTimeout(() => {
        this.addVendorPocModalOpen = true;
        setTimeout(() => {
          this.addVendorPocModalOpen = false;
        }, 0);
      }, 0);
    }, 0);
  }
  async onSubmit(data: any): Promise<void> {
    const employee = await this.authService.getEmployeeDetails();
    const userName = employee?.employeeName || employee?.email || 'Unknown';
    const body = {
      vendorId: data.vendorId,
      pointOfContact: data.pointOfContact || data.vendorRep,
      contact: data.contact,
      email: data.email,
      status: data.status,
      createdBy: userName,
      updatedBy: userName,
    };
    this.accessControlService.saveVendorRepresentative(body, 'post').subscribe({
      next: (res) => {
        this.loadVendorRepresentatives();
      },
      error: (err) => {
        this.errorHandler.logError(err);
      },
    });
    this.closeAddVendorPocModal();
  }

  navigateToOpsVendorDashboard(): void {
    this.router.navigate([NavigationRoutes.OpsVendorManagement]);
  }
}
